import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ErrorMessage } from '../../common';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { useGetAddressesQuery } from '../../../redux/apis';
import AddressSelection from '../../address/AddressDropdown';
import { setSameDayGroupAddress, setSameDayGroupTimeSlot } from '../../../redux/slices/checkoutSlice';
import { getProductImageUrl } from '../../../utils/productHelpers';

const SameDayMethodSection = ({ title, items, onComplete, setError }) => {
  const dispatch = useDispatch();
  const [address, setAddress] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Get user addresses
  const { 
    data: addresses = [], 
    isLoading: addressesLoading, 
    isSuccess: addressesSuccess 
  } = useGetAddressesQuery(undefined, {
    skip: !useSelector(state => state.auth?.user)
  });
  
  // Handle address change
  const handleAddressChange = (e) => {
    if (!e || !e.target) return;
    
    const addressId = e.target.value;
    if (addressId === 'new') {
      // Handle new address case
      return;
    }
    
    // Find the selected address from our addresses array
    const selectedAddress = addresses.find(addr => addr.id === addressId);
    if (selectedAddress) {
      setAddress(selectedAddress);
      
      dispatch(setSameDayGroupAddress({
        address: selectedAddress
      }));
      
      // Fetch time slots for same-day delivery
      fetchTimeSlots(selectedAddress);
    }
  };
  
  // Fetch available time slots for same-day delivery
  const fetchTimeSlots = async (address) => {
    if (!address) return;
    
    setLoading(true);
    
    try {
      // In a real implementation, this would call an API
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Check current time to determine available slots
      const now = new Date();
      const currentHour = now.getHours();
      
      // Only show time slots at least 3 hours from now
      // Same day delivery time slots from now until 10pm
      const slots = [];
      const endHour = 22; // 10 PM cutoff
      
      for (let hour = currentHour + 3; hour <= endHour; hour++) {
        // Skip if past end hour
        if (hour > endHour) continue;
        
        const displayTime = hour < 12 
          ? `${hour}:00 - ${hour + 1}:00 AM` 
          : hour === 12 
            ? '12:00 - 1:00 PM' 
            : `${hour - 12}:00 - ${hour - 11}:00 PM`;
            
        slots.push({
          id: `slot_${hour}`,
          time: `${hour}:00-${hour + 1}:00`,
          display: displayTime,
          fee: 12.99
        });
      }
      
      setTimeSlots(slots);
      
      // If we have slots, select the first one by default
      if (slots.length > 0) {
        setSelectedTimeSlot(slots[0]);
        dispatch(setSameDayGroupTimeSlot({
          timeSlot: slots[0]
        }));
      }
      
      setLoading(false);
    } catch (err) {
      setError('Unable to fetch delivery time slots. Please try again.');
      setLoading(false);
    }
  };
  
  // Handle time slot selection
  const handleTimeSlotSelect = (slot) => {
    setSelectedTimeSlot(slot);
    
    dispatch(setSameDayGroupTimeSlot({
      timeSlot: slot
    }));
  };
  
  // Update completion status
  useEffect(() => {
    const isComplete = address && selectedTimeSlot;
    if (onComplete) {
      onComplete(isComplete);
    }
  }, [address, selectedTimeSlot, onComplete]);
  
  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="font-medium text-lg mb-3">{title}</h3>
      
      {/* Show item thumbnails */}
      <div className="flex flex-wrap gap-2 mb-4">
        {items.map(item => (
          <div key={item.id || item.cartItemIndex} className="w-16">
            <div className="h-16 w-16 overflow-hidden rounded-md">
              <img 
                src={getProductImageUrl(item)}
                alt={item.name || item.product?.name}
                className="h-full w-full object-cover object-center"
              />
            </div>
            <div className="text-xs mt-1 truncate text-center">
              {item.name || item.product?.name}
            </div>
          </div>
        ))}
      </div>
      
      {/* Address Selection */}
      <div className="mb-4">
        <h4 className="text-sm font-medium mb-2">Delivery Address</h4>
        {addressesLoading ? (
          <LoadingSpinner />
        ) : (
          <AddressSelection 
            value={address?.id || ''}
            onChange={handleAddressChange}
            name="sameDayAddress"
            label=""
            placeholder="Select an address"
            showAddNew={true}
            required={true}
          />
        )}
      </div>
      
      {/* Only show time slots when address is selected */}
      {address && (
        <div>
          <h4 className="text-sm font-medium mb-2">Available Delivery Windows</h4>
          {loading ? (
            <LoadingSpinner />
          ) : timeSlots.length === 0 ? (
            <div className="p-3 border border-yellow-300 bg-yellow-50 rounded-md">
              <p className="text-yellow-700">
                No same-day delivery slots available for today. Please try standard shipping.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {timeSlots.map(slot => (
                <div
                  key={slot.id}
                  className={`p-3 border rounded flex justify-between items-center cursor-pointer ${selectedTimeSlot?.id === slot.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
                  onClick={() => handleTimeSlotSelect(slot)}
                >
                  <div>
                    <p className="font-medium">Today, {slot.display}</p>
                    <p className="text-sm text-gray-500">Same-day delivery</p>
                  </div>
                  <div className="font-medium">${slot.fee.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SameDayMethodSection; 